import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaApple, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import "./SoftwareDownloads.css";
import axios from 'axios';
import { Base_Url } from '../config';
import JoditEditor from 'jodit-react';
import { useSelector } from 'react-redux';
import { FcDocument } from "react-icons/fc";

const PLATFORM_ICONS = {
  Windows: '🪟',
  Linux: '🐧',
  Mac: <FaApple size={18} className='mb-1' />,
  Documentation: <FcDocument size={20} className='mb-1' />,
};

const DownloadTable = ({ data, onEdit, onDelete }) => {
  if (!data || data.length === 0) return <p>No data available.</p>;

  return (
    <div className="custom-table-container">
      <table className="table custom-table">
        <thead>
          <tr>
            <th className="text-center">Version</th>
            <th className="text-center">Platform</th>
            <th>Download Link</th>
          </tr>
        </thead>
        <tbody>
          {data.map((version, versionIndex) => (
            <React.Fragment key={version.VersionId}>
              {version.Platforms.map((platform, platformIndex) => (
                <tr key={platformIndex} className={versionIndex % 2 === 0 ? 'even-row' : 'odd-row'}>
                  {platformIndex === 0 && (
                    <td className="version-cell text-center" rowSpan={version.Platforms.length}>
                      <span className="version-badge">{version.VersionName}</span>
                    </td>
                  )}
                  <td className="platform-cell text-center">
                    <div className={`platform-badge ${platform.Platform?.toLowerCase() || ''}`}>
                      <span className="platform-icon">
                        {platform.Platform === 'Windows' ? PLATFORM_ICONS.Windows :
                          platform.Platform === 'Linux' ? PLATFORM_ICONS.Linux : 
                          platform.Platform === 'Documentation' ? PLATFORM_ICONS.Documentation : 
                            platform.Platform === 'Mac' ? PLATFORM_ICONS.Mac : ''}
                      </span>
                      <span className="platform-text">{platform.Platform}</span>
                    </div>
                  </td>
                  <td className="download-cell text-center">
                    <div className="d-flex flex-column">
                      {platform.DownloadLinks.map((link, linkIdx) => (
                        <a key={linkIdx} href={link.link} className="download-link mb-2">
                          <span className="disk-label">{link.name}</span>
                        </a>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SoftwareDownloads = () => {
  const navigate = useNavigate();
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [softwareData, setSoftwareData] = useState([]);
  const [editingContent, setEditingContent] = useState(null);
  const [editorContent, setEditorContent] = useState('');

  const fetchData = async () => {
    try {
      const [softwareRes, versionRes, platformRes, diskRes, sciSoftPageRes] = await Promise.all([
        axios.get(`${Base_Url}/software`),
        axios.get(`${Base_Url}/version`),
        axios.get(`${Base_Url}/platform`),
        axios.get(`${Base_Url}/disk`),
        axios.get(`${Base_Url}/scisoftpages`)
      ]);
  
      const structuredData = softwareRes.data.map(software => {
        const versions = versionRes.data.filter(v => v.software_id === software.id);
  
        // Sort versions in descending order based on year
        const sortedVersions = versions.sort((a, b) => {
          // Extract year and optional letter (like '2024a' -> ['2024', 'a'])
          const aMatch = a.version_name.match(/^(\d+)([a-z]?)$/i);
          const bMatch = b.version_name.match(/^(\d+)([a-z]?)$/i);
          
          const aYear = aMatch ? parseInt(aMatch[1]) : 0;
          const bYear = bMatch ? parseInt(bMatch[1]) : 0;
          
          // First compare by year
          if (bYear !== aYear) {
            return bYear - aYear;
          }
          
          // If years are equal, compare by optional letter
          const aLetter = aMatch && aMatch[2] ? aMatch[2].toLowerCase() : '';
          const bLetter = bMatch && bMatch[2] ? bMatch[2].toLowerCase() : '';
          
          return bLetter.localeCompare(aLetter);
        });
  
        const versionData = sortedVersions.map(version => {
          const platforms = platformRes.data.filter(p => p.version_id === version.id);
  
          const platformData = platforms.map(platform => {
            const disks = diskRes.data.filter(d => d.platform_id === platform.id);
  
            return {
              Platform: platform.platform_name,
              DownloadLinks: disks.map(disk => ({
                name: `${disk.disk_size}`,
                link: disk.download_link
              }))
            };
          });
  
          return {
            VersionName: version.version_name,
            VersionId: version.id,
            Platforms: platformData
          };
        });
  
        const setupGuide = sciSoftPageRes.data.find(page => page.software_id === software.id);
  
        return {
          ID: software.id,
          MainHeading: software.name,
          Versions: versionData,
          SetupGuide: setupGuide ? setupGuide.content : 'No setup guide available.',
          SetupGuideId: setupGuide ? setupGuide.id : null
        };
      });
  
      setSoftwareData(structuredData);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditVersion = (versionId) => {
    navigate(`/editscientific/${versionId}`);
  };

  const handleEditSetupGuide = (content, id) => {
    setEditingContent(id);
    setEditorContent(content);
  };

  const userfromwebmaster = useSelector((state) => state.user);
  const statecsc = useSelector((state) => state.statecsc);

  const handleSave = async (softwareId, setupGuideId) => {
    try {
      if (setupGuideId) {
        await axios.put(`${Base_Url}/scisoftpages/${setupGuideId}`, {
          id: setupGuideId,
          software_id: softwareId,
          content: editorContent
        });
      } else {
        await axios.post(`${Base_Url}/scisoftpages`, {
          software_id: softwareId,
          content: editorContent
        });
      }
      setEditingContent(null);
      fetchData();
    } catch (error) {
      console.error("Error saving setup guide", error);
    }
  };

  const handleDeleteSoftware = async (softwareId) => {
    try {
      await axios.delete(`${Base_Url}/software/${softwareId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting software", error);
    }
  };

  const handleDeleteSetupGuide = async (setupGuideId) => {
    try {
      await axios.delete(`${Base_Url}/scisoftpages/${setupGuideId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting setup guide", error);
    }
  };

  return (
    <div className="container py-4">

      {/* Contact Email Section - Enhanced */}

      {/* Warning Alert */}
      <div className="alert alert-danger mb-4 m-0" role="alert">
        <h4 className="alert-heading">WARNING! DO NOT DOWNLOAD WHILE YOUR PROXY CONNECTION IS ACTIVE!</h4>
        <p className="mb-0">This is an internal site and all files can be downloaded without logging in to the proxy server</p>
      </div>

      {/* Additional Information Section */}
      <div className="alert alert-info mb-4" role="alert">
        <h4 className="alert-heading">Explore Beyond Basics</h4>
        <p>
          Here you can find information for all the software that is licensed for use by the IITD community.
          We provide basic support in installation of these software. However, for detailed/technical support,
          the respective vendors/software support teams need to be contacted.
        </p>
      </div>
      {/* <div className="alert alert-warning mb-4" role="alert">
        <h4 className="alert-heading">Research software pre-requisites</h4>
        <p className='mb-1'> In case the software is not listed here, the same can be requested from CSC.
        </p>
        <p> Minimum of 3 academic units and a minimum of 5 faculty members need to recommend the software, along with number of licenses required.
        </p>
      </div> */}
   <div className="alert alert-warning mb-4" role="alert">
        <h4 className="alert-heading">Software pre-requisites</h4>
        <p className='mb-1'> 1. If a software is needed for research purposes, then kindly send a note to Head CSC. There should at least be 5 active users (faculty) of the software across 3 academic units. 
        </p>
        <p className='mb-0'>2. If a software is needed for teaching, then kindly route the request via the respective DFB. 
        </p>
        <p>3. Some of our tool licenses are expected to expire shortly. Requesting the relevant AUs to kindly let us know about the future requirements.
        </p>
      </div>

      <div className="contact-email-section mb-4 p-4 rounded-3" style={{
 background: 'linear-gradient(135deg, #faf9f9f1 0%, #ecececff 100%)',
  border: 'none',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
}}>
  <div className="row align-items-center">
    <div className="col-md-8">
      <h4 className=" mb-2 fw-bold" style={{color:"#666464"}}>
        <i className="fas fa-envelope me-2"></i>
        Contact Us
      </h4>
    </div>
    <div className="col-md-4 text-md-end">
      <div className="d-inline-block bg-white px-3 py-2 rounded-2">
        <strong className="text-dark me-2">Email:</strong>
        <a 
          href="mailto:scisoft@cc.iitd.ac.in" 
          className="fw-bold text-decoration-none"
          style={{ color: '#764ba2' }}
          onMouseOver={(e) => e.target.style.color = '#667eea'}
          onMouseOut={(e) => e.target.style.color = '#764ba2'}
        >
          scisoft@cc.iitd.ac.in
        </a>
      </div>
    </div>
  </div>
</div>
      {(userfromwebmaster.userExists || statecsc.userState === 0) && (
        <div className="ms-auto">
          <button
            className="btn btn-primary btn-sm me-2"
            onClick={() => navigate("/add-scisoft")}
          >
            <FaPlus /> Add
          </button>
        </div>
      )}

      {/* Software Accordion */}
      <div className="accordion" id="softwareAccordion">
        {softwareData.map((item, index) => (
          <div className="accordion-item mb-1"   key={index}>
            <h2 className="accordion-header">
              <button
              style={{background:"#a32424"}}
                className={`accordion-button sciaccordion ${activeAccordion !== item.ID ? 'collapsed' : ''}`}
                type="button"
                onClick={() => setActiveAccordion(activeAccordion !== item.ID ? item.ID : '')}
              >
                {item.MainHeading}

                {(userfromwebmaster.userExists || statecsc.userState === 0) && (
                  <div className="ms-auto">
                    <button
                      className="btn btn-primary btn-sm me-2"
                      onClick={() => handleEditVersion(item.ID)}
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteSoftware(item.ID)}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                )}
              </button>
            </h2>
            <div
              className={`accordion-collapse collapse ${activeAccordion === item.ID ? 'show' : ''}`}
            >
              <div className="accordion-body">
                <div className="row">
                  <div className="col-lg-7">
                    <DownloadTable
                      data={item.Versions}
                      onEdit={handleEditVersion}
                      onDelete={handleDeleteSetupGuide}
                    />
                  </div>
                  <div className="col-lg-5">
                    <div className="setup-guide-container">
                      <h5>Setup Guide</h5>
                      {editingContent === item.ID ? (
                        <>
                          <JoditEditor
                            value={editorContent}
                            onChange={newContent => setEditorContent(newContent)}
                          />
                          <button
                            className="btn btn-success btn-sm mt-2"
                            onClick={() => handleSave(item.ID, item.SetupGuideId)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-secondary btn-sm mt-2 ms-2"
                            onClick={() => setEditingContent(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <div dangerouslySetInnerHTML={{ __html: item.SetupGuide }} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SoftwareDownloads;